import React, { forwardRef } from 'react';
import { 
  Box, 
  Tabs,
  Tab,
  Button
} from '@mui/material';
import PersonalInformationTab from '../../../components/applicationFormTabs/PersonalInformationTab';
import CounsellingInformationTab from "../../../components/applicationFormTabs/CounsellingInformationTab";
import EducationTab from '../../../components/applicationFormTabs/EducationTab';
import RefereeTab from '../../../components/applicationFormTabs/RefereeTab';
import { FormData } from '../../../types/forms';
import api from '../../../utils/api';
import { handleSaveSection } from './SectionHandlers';
import { validateSectionForSave } from './validators';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = forwardRef<HTMLDivElement, TabPanelProps>(
  ({ children, value, index, ...other }, ref) => {
    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        ref={ref}
        {...other}
      >
        {value === index && (
          <Box sx={{ p: 3 }}>
            {children}
          </Box>
        )}
      </div>
    );
  }
);

TabPanel.displayName = 'TabPanel';

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

interface TabsContainerProps {
  activeTab: number;
  handleTabChange: (event: React.BaseSyntheticEvent, newValue: number) => void;
  formData: FormData;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  saveStatus: { [key: string]: 'idle' | 'saving' | 'saved' | 'error' };
  setSaveStatus: React.Dispatch<React.SetStateAction<{ [key: string]: 'idle' | 'saving' | 'saved' | 'error' }>>;
  setActiveTab: (tab: number) => void;
  token: string | undefined;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  isSubmitted: boolean;
  submitting: boolean;
  handleSubmitForm: () => Promise<void>;
  handlePersonalInfoChange: (data: any) => void;
  handleCounsellingInfoChange: (field: string, value: any) => void;
  handleEducationInfoChange: (field: string, value: any) => void;
  handleRefereeInfoChange: (data: any) => void;
}

const TabsContainer: React.FC<TabsContainerProps> = ({
  activeTab,
  handleTabChange,
  formData,
  setFormData,
  saveStatus,
  setSaveStatus,
  setActiveTab,
  token,
  setError,
  isSubmitted,
  submitting,
  handleSubmitForm,
  handlePersonalInfoChange,
  handleCounsellingInfoChange,
  handleEducationInfoChange,
  handleRefereeInfoChange
}) => {
  const handleSaveSectionWrapper = async (section: string) => {
    await handleSaveSection(
      section,
      formData,
      token,
      setSaveStatus,
      setError,
      validateSectionForSave,
      api
    );
  };

  return (
    <>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="application form tabs">
          <Tab label="Personal Information" {...a11yProps(0)} />
          <Tab label="Counselling Information" {...a11yProps(1)} />
          <Tab label="Education" {...a11yProps(2)} />
          <Tab label="References" {...a11yProps(3)} />
        </Tabs>
      </Box>

      <TabPanel value={activeTab} index={0}>
        <PersonalInformationTab
          formData={formData.personal}
          onChange={handlePersonalInfoChange}
          onNext={() => setActiveTab(1)}
          onSave={() => handleSaveSectionWrapper('personal')}
          saveStatus={saveStatus['personal'] === 'idle' || saveStatus['personal'] === 'saving' ? null : saveStatus['personal']}
        />
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <CounsellingInformationTab
          formData={formData.counselling}
          onChange={handleCounsellingInfoChange}
          onNext={() => setActiveTab(2)}
          onBack={() => setActiveTab(0)}
          onSave={() => handleSaveSectionWrapper('counselling')}
          saveStatus={saveStatus['counselling'] === 'idle' || saveStatus['counselling'] === 'saving' ? null : saveStatus['counselling']}
        />
      </TabPanel>

      <TabPanel value={activeTab} index={2}>
        <EducationTab
          formData={formData.education}
          onChange={handleEducationInfoChange}
          onNext={() => setActiveTab(3)}
          onBack={() => setActiveTab(1)}
          onSave={() => handleSaveSectionWrapper('education')}
          saveStatus={saveStatus['education'] === 'idle' || saveStatus['education'] === 'saving' ? null : saveStatus['education']}
        />
      </TabPanel>

      <TabPanel value={activeTab} index={3}>
        <RefereeTab
          formData={formData.referee}
          onChange={handleRefereeInfoChange}
          onBack={() => setActiveTab(2)}
          onSave={() => handleSaveSectionWrapper('referee')}
          saveStatus={saveStatus['referee'] === 'idle' || saveStatus['referee'] === 'saving' ? null : saveStatus['referee']}
        />
      </TabPanel>

      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmitForm}
          disabled={isSubmitted || submitting}
        >
          {isSubmitted ? 'Application Submitted' : submitting ? 'Submitting...' : 'Submit Application'}
        </Button>
      </Box>
    </>
  );
};

export default TabsContainer;
